import { useEffect, useState } from "react";
import { ListNotificationsUseCase } from "../../Usecase/Notifications/list-notifications-usecase";
import { INotificationEntity } from "../../Services/api/notifications/notifications.interface";
import { useToast } from "../../Hooks/ToasContext";
import { MarkAsReadNotificationsUseCase } from "../../Usecase/Notifications/mark-as-read-notifications-usecase";
import { useAuth } from "../../Hooks/Auth";
import { Environment } from "../../Config/constants";


interface IUseNotificationMenuProps {
    listNotificationsUseCase: ListNotificationsUseCase
    markAsReadNotificationUseCase?: MarkAsReadNotificationsUseCase
}

export function useNotifications({ listNotificationsUseCase, markAsReadNotificationUseCase }: IUseNotificationMenuProps) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(1)
    const [data, setData] = useState<INotificationEntity[]>([])
    const [totalUnreadNotifications, setTotalUnreadNotifications] = useState(0)
    const [totalRegisters, setTotalRegisters] = useState(0)
    const { showToast } =  useToast()
    const { user } = useAuth()

    const getUserIdFromToken = () => {
      try {
        const token = localStorage.getItem(`${Environment.CUSTOMER}:token`);
        if (!token) return null;
        const payloadBase64 = token.split('.')[1];
        if (!payloadBase64) return null;
        const decoded = JSON.parse(atob(payloadBase64));
        return decoded.userId || decoded.sub || decoded.id;
      } catch (error) {
        console.error("Erro ao decodificar token para obter ID do usuário:", error);
        return null;
      }
    };
   
    const handleClick = (event: any) => {
      setAnchorEl(event.currentTarget);
    };
  
    const handleClose = () => {
      setAnchorEl(null);
    };
  
  
  
    const loadNotifications = async ({ page: currentPage, searchBy }: any) => {
          
      try {
       
        
        const listResult = await listNotificationsUseCase.handle({ page: 1 });
  
        if (listResult.isFailure) {
          showToast(String(listResult?.error?.error ?? 'Erro ao carregar notificações'), 'error');
          return;
        }
  
       const list = listResult.getValue()
       const notifications = list?.data || []
       const userId = getUserIdFromToken();

       // Filtramos para mostrar apenas as que o usuário ainda não viu (Limpar = Esconder)
       // Verificamos por username e por ID (que vem do token) para garantir a compatibilidade
       const activeNotifications = notifications.filter(n => {
         const isSeenByUsername = user?.username && n.usersRead?.includes(user.username);
         const isSeenByUserId = userId && n.usersRead?.includes(userId);
         return !isSeenByUsername && !isSeenByUserId;
       });

      setTotalUnreadNotifications(activeNotifications.length)
      setData(activeNotifications)
      setTotalRegisters(list?.total || 0)
       
  
  
       setPage(currentPage)
      } catch (error) {
        console.log("error listCustomers function: ", error)
      } finally {
        setLoading(false);
      }
  };

  const markAllAsRead = async () => {
    if (!markAsReadNotificationUseCase || data.length === 0) return;

    // Atualização Otimista
    const previousData = [...data];
    setData([]);
    setTotalUnreadNotifications(0);

    try {
      const promises = data.map(notification => 
        markAsReadNotificationUseCase.handle(notification.id)
      );

      await Promise.all(promises);
      showToast('Todas as notificações foram marcadas como lidas', 'success');
      
      // Pequeno delay para garantir que o backend processou tudo antes do reload
      setTimeout(() => loadNotifications({ page: 1 }), 500);
    } catch (error) {
      console.error("Erro ao limpar notificações:", error);
      setData(previousData);
      setTotalUnreadNotifications(previousData.length);
      showToast('Erro ao limpar algumas notificações', 'error');
    }
  };

  const markSingleAsRead = async (notificationId: string) => {
    if (!markAsReadNotificationUseCase) return;

    // Atualização Otimista: Remove da lista local e diminui o contador imediatamente
    const previousData = [...data];
    const itemToRemove = data.find(n => n.id === notificationId);
    
    setData(prev => prev.filter(n => n.id !== notificationId));
    if (itemToRemove) {
      setTotalUnreadNotifications(prev => Math.max(0, prev - 1));
    }

    try {
      const response = await markAsReadNotificationUseCase.handle(notificationId);
      
      if (response.isFailure) {
        // Se falhar no servidor, restauramos o estado
        setData(previousData);
        setTotalUnreadNotifications(previousData.length);
        showToast(String(response?.error?.error || 'Erro ao sincronizar notificação'), 'error');
        return;
      }

      // Sincroniza com o backend após um pequeno delay
      setTimeout(() => loadNotifications({ page: 1 }), 500);
    } catch (error) {
      console.error("Erro ao marcar notificação como lida:", error);
      setData(previousData);
      setTotalUnreadNotifications(previousData.length);
    }
  };
  
    useEffect(() => {
     
      loadNotifications({ page: 1 })
  
    }, [])

    return {
        listNotificationsUseCase,
        anchorEl,
        open,
        handleClick,
        handleClose,
        loading,
        data,
        totalRegisters,
        page,
        loadNotifications,
        totalUnreadNotifications,
        markAllAsRead,
        markSingleAsRead
    }
}